/**
 * StoryPreviewButton — pair of buttons that let the user both PREVIEW the
 * generated 1080×1920 Instagram Story image and SHARE it via the native
 * share sheet (Web Share Level 2).
 *
 * The preview button is critical for diagnosing bad-render reports: if
 * the modal shows the correct image but Instagram shows an old one, the
 * problem is 100% on the Instagram / device-cache side, not our server.
 */
import React, { useState } from "react";
import { Instagram, Eye, X, RefreshCw, ExternalLink } from "lucide-react";
import { shareStoryImage } from "@/lib/share";

export default function StoryPreviewButton({ url, filename, title, text, testIdShare, testIdPreview }) {
  const [open, setOpen] = useState(false);
  // Each preview-open gets a fresh timestamp so the browser never shows
  // an old cached image inside the modal.
  const [bust, setBust] = useState(() => Date.now());
  const previewUrl = url
    ? url + (url.includes("?") ? "&" : "?") + `_t=${bust}`
    : "";

  const refresh = () => setBust(Date.now());

  return (
    <>
      <button
        type="button"
        onClick={() => { refresh(); setOpen(true); }}
        aria-label="Hikaye g\u00f6rselini \u00f6nizle"
        data-testid={testIdPreview || "share-story-preview"}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:border-[#e11d2a] hover:text-[#e11d2a] rounded-full text-xs font-bold text-gray-700 transition"
        title="Instagram'a göndermeden önce önizle"
      >
        <Eye className="w-3.5 h-3.5" /> Önizle
      </button>

      <button
        type="button"
        onClick={() => shareStoryImage({ url, filename, title, text })}
        aria-label="Instagram Story olarak payla\u015f"
        data-testid={testIdShare || "share-story"}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-tr from-pink-500 via-purple-500 to-yellow-400 hover:brightness-110 text-white rounded-full text-xs font-bold"
        title="1080×1920 dikey görsel — doğrudan Instagram Story'ye paylaş"
      >
        <Instagram className="w-3.5 h-3.5" /> Story Paylaş
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[1000] bg-black/85 flex items-center justify-center p-3"
          role="dialog"
          aria-modal="true"
          aria-label="Hikaye önizlemesi"
          onClick={() => setOpen(false)}
          data-testid="story-preview-modal"
        >
          <div
            className="relative w-full max-w-[380px] rounded-xl overflow-hidden bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-2 right-2 z-10 p-2 rounded-full bg-white/15 hover:bg-white/25 text-white"
              aria-label="Kapat"
              data-testid="story-preview-close"
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={previewUrl}
              alt="Instagram Story önizlemesi"
              className="w-full h-auto block"
              /* Keep the aspect ratio locked so the modal always renders at
               * 9:16 even while the image is still loading. */
              style={{ aspectRatio: "9 / 16", objectFit: "cover" }}
              data-testid="story-preview-image"
            />
            <div className="flex items-center justify-between gap-2 p-3 bg-white">
              <div className="text-[11px] text-gray-500 leading-tight">
                1080×1920 · Instagram Story oranı<br />
                Gördüğünüz görsel paylaşılacak dosyadır.
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={refresh}
                  className="p-1.5 border border-gray-200 rounded hover:bg-gray-50 text-gray-600"
                  title="Yeniden üret"
                  aria-label="Yeniden üret"
                  data-testid="story-preview-refresh"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="p-1.5 border border-gray-200 rounded hover:bg-gray-50 text-gray-600"
                  title="Yeni sekmede aç"
                  aria-label="Yeni sekmede aç"
                  data-testid="story-preview-open"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  type="button"
                  onClick={() => { setOpen(false); shareStoryImage({ url, filename, title, text }); }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-tr from-pink-500 via-purple-500 to-yellow-400 text-white rounded text-xs font-bold"
                  data-testid="story-preview-share"
                >
                  <Instagram className="w-3.5 h-3.5" /> Paylaş
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
